import React, { useState } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #dc3545;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`;

const ComponentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ComponentItem = styled.label`
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 2px solid ${props => props.$checked ? '#dc3545' : '#e0e0e0'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$checked ? '#fff5f5' : 'white'};

  &:hover {
    border-color: ${props => props.$checked ? '#dc3545' : '#999'};
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin-right: 1rem;
  cursor: pointer;
  accent-color: #dc3545;
`;

const ComponentInfo = styled.div`
  flex: 1;
`;

const ComponentName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
`;

const ComponentType = styled.div`
  font-size: 0.875rem;
  color: #666;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background: #5a6268;
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background: #c82333;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const InfoText = styled.p`
  color: #666;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
`;

const OutOfStockModal = ({ lineData, onClose, onSave }) => {
  // Initialize with existing out of stock components if any
  const [selectedComponents, setSelectedComponents] = useState(() => {
    // Get existing out of stock components from the first component's metadata
    const existingOutOfStock = lineData.components[0]?.metadata?.outOfStockComponents || [];

    // Map them to the format we need
    return existingOutOfStock.map(comp => ({
      id: comp.id,
      lineType: comp.lineType,
      productId: comp.productId,
      productName: comp.productName
    }));
  });

  const handleToggleComponent = (component) => {
    setSelectedComponents(prev => {
      const isSelected = prev.some(c => c.id === component.id);
      if (isSelected) {
        return prev.filter(c => c.id !== component.id);
      } else {
        return [...prev, {
          id: component.id,
          lineType: component.lineType,
          productId: component.productId,
          productName: component.product?.name || 'Producto desconocido'
        }];
      }
    });
  };

  const handleSave = () => {
    onSave(selectedComponents);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            🚨 Componentes Sin Stock
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <InfoText>
          Selecciona los componentes que están sin stock para esta línea de pedido:
        </InfoText>

        <ComponentsList>
          {lineData.components.map((component) => {
            const isChecked = selectedComponents.some(c => c.id === component.id);

            return (
              <ComponentItem
                key={component.id}
                $checked={isChecked}
              >
                <Checkbox
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleToggleComponent(component)}
                />
                <ComponentInfo>
                  <ComponentName>{component.product?.name || 'Producto desconocido'}</ComponentName>
                  <ComponentType>
                    {component.lineType}
                    {component.width && component.height && ` - ${component.width}×${component.height}cm`}
                  </ComponentType>
                </ComponentInfo>
              </ComponentItem>
            );
          })}
        </ComponentsList>

        <ModalActions>
          <CancelButton onClick={onClose}>Cancelar</CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={selectedComponents.length === 0}
          >
            Marcar {selectedComponents.length > 0 && `(${selectedComponents.length})`} Sin Stock
          </SaveButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default OutOfStockModal;
